'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useRef } from 'react';
import { useSidebarIndicator } from './useSidebarIndicator';
import { useHoverTip } from './HoverTip';
import { RoleGuard } from '@/components/rbac/RoleGuard';
import { LayoutIcon } from './icons';
import { PendingBadge } from '@/components/admin/PendingBadge';
import type { NavigationItem, NavigationSection } from './types';
import styles from './SidebarMenu.module.css';

interface SidebarMenuProps {
	sections: NavigationSection[];
	compact?: boolean;
	onNavigate?: () => void;
	/** Optional pending-count badge per item href (e.g. { '/admin': 5 }). */
	badges?: Record<string, number>;
}

export function SidebarMenu({ sections, compact = false, onNavigate, badges }: SidebarMenuProps) {
	const pathname = usePathname();
	const navRef = useRef<HTMLElement>(null);
	const indicator = useSidebarIndicator(navRef, pathname, compact);

	return (
		<nav ref={navRef} className={`${styles.nav} ${indicator.ready ? styles.navReady : ''}`}>
			<div
				className={styles.activeIndicator}
				style={{
					top: `${indicator.top}px`,
					height: `${indicator.height}px`,
					opacity: indicator.opacity,
					transition: indicator.animate
						? 'top 0.4s cubic-bezier(0.4, 0, 0.2, 1), height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease'
						: 'none'
				}}
			/>
			<ul className={styles.list}>
				{sections.flatMap(section => section.items).map((item, index) => (
					<li key={`${item.href}-${index}`}>
						<GuardedNavigationItem item={item}>
							<NavigationLink
								item={item}
								compact={compact}
								active={isActivePath(pathname, item.href)}
								badge={badges?.[item.href]}
								onNavigate={onNavigate}
							/>
						</GuardedNavigationItem>
					</li>
				))}
			</ul>
		</nav>
	);
}

interface NavigationLinkProps {
	item: NavigationItem;
	compact: boolean;
	active: boolean;
	badge?: number;
	onNavigate?: () => void;
}

/**
 * Un destino del menú.
 *
 * Es un componente y no un bloque suelto dentro del `map` porque cada fila necesita
 * su propio estado de etiqueta, y un hook no se puede llamar en un bucle.
 *
 * La etiqueta sólo sale con la barra colapsada: desplegada, el nombre ya está
 * escrito al lado del icono y repetirlo flotando sería ruido. Y no lleva `title`,
 * a propósito — ver `HoverTip.tsx`: el nativo aparecería uno o dos segundos después,
 * encima de éste. El nombre accesible sigue viniendo del `aria-label`.
 */
function NavigationLink({ item, compact, active, badge, onNavigate }: NavigationLinkProps) {
	const hoverTip = useHoverTip(compact ? item.label : null, 'right');

	return (
		<Link
			href={item.href}
			onClick={onNavigate}
			className={`${styles.item} ${active ? styles.itemActive : styles.itemInactive}`}
			aria-current={active ? 'page' : undefined}
			aria-label={item.label}
			{...hoverTip.handlers}
		>
			<div className={styles.iconWrapper}>
				<LayoutIcon
					name={item.icon}
					className={styles.icon}
					size={40}
					strokeWidth={1.8}
				/>
				{badge ? (
					<PendingBadge
						count={badge}
						className={styles.iconBadge}
						aria-label={`${badge} pendientes en ${item.label}`}
					/>
				) : null}
			</div>
			<span
				className={`${styles.textContainer} ${compact ? styles.textContainerCompact : styles.textContainerExpanded}`}
			>
				<span className={styles.textLabel}>
					{item.label}
				</span>
			</span>
			{hoverTip.tip}
		</Link>
	);
}

function GuardedNavigationItem({ item, children }: { item: NavigationItem; children: React.ReactNode; }) {
	if (!item.allowedRoles?.length) return <>{children}</>;
	return (
		<RoleGuard
			allowedRoles={item.allowedRoles}
			fallback={null}
			loadingFallback={null}
		>
			{children}
		</RoleGuard>
	);
}

function isActivePath(pathname: string, href: string) {
	if (href === '/') return pathname === '/';
	return pathname === href || pathname.startsWith(`${href}/`);
}
